// The six cards of the Blunders or Misses screen as rows: each row a group
// of mistakes (`mistakeKeys` of `@chess/api-mistakes`) with its games, its
// cost and the key that opens those games in the library
// (`/library?mistake=<key>`). Pure; the cards draw these rows, the tests
// read them. Wording per kind lives here too, so both screens come from the
// same code.
import { MISTAKE_STANDINGS, type MistakeStanding, type MoveKind } from '@chess/api-model';
import {
  CLOCK_BUCKETS,
  familyLabel,
  MATERIAL_LABEL,
  MATERIALS,
  mistakeKey,
  MOTIF_FAMILIES,
  SEVERITIES,
  SEVERITY_LABEL,
  type ClockBucket,
  type Material,
  type MistakeKey,
  type MistakeWeek,
  type Severity,
} from '@chess/api-mistakes';
import { groupOf, type MistakeGroups } from './MistakeGroupsAggregator.ts';

/** One bar of a card. */
export interface GroupRow {
  /** The group's library key, `<kind>-<dimension>-<value>`. */
  key: MistakeKey;
  label: string;
  /** Games with at least one such mistake: what the bar opens. */
  games: number;
  mistakes: number;
  /** Pawns, in the decisive zone. */
  cost: number;
}

/** The screens' nouns and the cards' wording, per kind. */
export const KIND_WORDS: Record<MoveKind, { one: string; many: string; patternsTitle: string; materialTitle: string; costVerb: string }> = {
  blunder: { one: 'blunder', many: 'blunders', patternsTitle: 'Patterns that cost the most', materialTitle: 'What they cost', costVerb: 'lost' },
  miss: { one: 'miss', many: 'misses', patternsTitle: 'Chances that cost the most', materialTitle: 'What they missed', costVerb: 'missed' },
};

/** The standing before the move, in the chips' and the cards' words. */
export const STANDING_SHORT: Record<MistakeStanding, string> = { winning: 'Ahead', level: 'Level', losing: 'Behind' };
/** The clock before the move, short. */
export const CLOCK_SHORT: Record<ClockBucket, string> = { under1: 'Under 1 min', '1to3': '1–3 min', '3plus': '3+ min' };
/** The weeks, short, for the columns. */
export const WEEK_SHORT: Record<MistakeWeek, string> = { 0: '22–30 d', 1: '15–21 d', 2: '8–14 d', 3: 'Last 7 d' };

const row = (r: MistakeGroups, key: MistakeKey, label: string): GroupRow => ({ key, label, ...groupOf(r, key) });

/** Patterns that cost the most lists at most this many families, the costliest: the ones to work on. */
export const MAX_FAMILY_ROWS = 5;

/**
 * Every motif family that has a mistake of the kind (`blunder-motif-pin`,
 * the family's games holding every variation's), the costliest first (then
 * the most games, then the families' order). A family with no mistake is
 * left out: twelve families, most of them empty in 30 days, would bury the
 * few that matter.
 */
export function rankedFamilyRows(r: MistakeGroups): GroupRow[] {
  const rows = MOTIF_FAMILIES.map((f) => row(r, mistakeKey(r.kind, 'motif', f), familyLabel(f)));
  return rows
    .map((x, i) => ({ x, i }))
    .filter(({ x }) => x.mistakes > 0)
    .sort((a, b) => b.x.cost - a.x.cost || b.x.games - a.x.games || a.i - b.i)
    .map(({ x }) => x);
}

/** Patterns that cost the most: the worst `MAX_FAMILY_ROWS` of `rankedFamilyRows`, so the card stays short; `familyNote` says when some are left out. */
export function familyRows(r: MistakeGroups): GroupRow[] {
  return rankedFamilyRows(r).slice(0, MAX_FAMILY_ROWS);
}

/** The patterns card's note: `scannedNote`, and "· worst 5 of 8 families" when families were left out. */
export function familyNote(r: MistakeGroups): string {
  const ranked = rankedFamilyRows(r).length;
  const base = scannedNote(r);
  return ranked > MAX_FAMILY_ROWS ? `${base} · worst ${MAX_FAMILY_ROWS} of ${ranked} families` : base;
}

/** How costly: decisive, serious, minor. */
export function severityRows(r: MistakeGroups): Array<GroupRow & { severity: Severity }> {
  return SEVERITIES.map((s) => ({ ...row(r, mistakeKey(r.kind, 'severity', s), SEVERITY_LABEL[s]), severity: s }));
}

/** Where you stood: ahead, level, behind before the move. */
export function standingRows(r: MistakeGroups): GroupRow[] {
  return MISTAKE_STANDINGS.map((s) => row(r, mistakeKey(r.kind, 'standing', s), STANDING_SHORT[s]));
}

/** On the clock: under a minute, one to three, three or more. */
export function clockRows(r: MistakeGroups): GroupRow[] {
  return CLOCK_BUCKETS.map((c) => row(r, mistakeKey(r.kind, 'clock', c), CLOCK_SHORT[c]));
}

/**
 * What they cost / missed: the pieces and mate, in value order. "No
 * material" is left out: those are the positional ones, which the patterns
 * card already shows (the Positional family), and the card stays at six rows.
 */
export const CARD_MATERIALS = MATERIALS.filter((m): m is Exclude<Material, 'none'> => m !== 'none');
export function materialRows(r: MistakeGroups): GroupRow[] {
  return CARD_MATERIALS.map((m) => row(r, mistakeKey(r.kind, 'material', m), MATERIAL_LABEL[r.kind][m]));
}

/** One column of Week by week: the week's mistakes per scanned game; undefined when no game of that week was scanned. */
export interface WeekColumn {
  key: MistakeKey;
  week: MistakeWeek;
  label: string;
  rate?: number;
  mistakes: number;
  scanned: number;
  /** Games of the week with such a mistake: what the column opens. */
  games: number;
}

/** Week by week, oldest first. */
export function weekColumns(r: MistakeGroups): WeekColumn[] {
  return r.weeks.map((w) => {
    const key = mistakeKey(r.kind, 'week', w.week);
    return { key, week: w.week, label: WEEK_SHORT[w.week], rate: w.scanned ? w.mistakes / w.scanned : undefined, mistakes: w.mistakes, scanned: w.scanned, games: groupOf(r, key).games };
  });
}

/** A card's note: "in 12 of 40 scanned games". */
export function scannedNote(r: Pick<MistakeGroups, 'games' | 'scanned'>): string {
  return `in ${r.games} of ${r.scanned} scanned ${r.scanned === 1 ? 'game' : 'games'}`;
}

/** Pawns, one decimal: "4.5". */
export const pawns = (p: number) => p.toFixed(1);
